import {
  Box,
  IconButton,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { EditFieldButton } from "@/shared/components";
import { isActionTimeoutValid, parseTimeout } from "@/shared/domain/actions";
import { InfoIcon } from "@/shared/foundation/icons/mui";
import { pluralize } from "@/shared/text";

import { handleTimeoutOnChangeState } from "./actionTimeoutState";

interface EditableTimeoutProps<UpdateType> {
  value: number | undefined;
  onChange: (updates: UpdateType) => void;
  setErrorText: (arg: string | null) => void;
  renderMarkdown?: boolean;
  disablePadding?: boolean;
  onHelperClick?: () => void;
  canEdit?: boolean;
  maxLength?: number;
}

const HELP_TEXT =
  "Action timeout, if defined, must be a number between 1 and 720 minutes.";

function isNotNil<T>(val: T): val is NonNullable<T> {
  return val !== undefined && val !== null;
}

export function EditableTimeout<UpdateType>({
  value,
  onChange,
  setErrorText,
  disablePadding,
  onHelperClick,
  canEdit,
}: EditableTimeoutProps<UpdateType>) {
  const theme = useTheme();
  const [showEditableIcon, setShowEditableIcon] = React.useState(false);
  const [editable, setEditable] = React.useState(false);
  const [currentValue, setCurrentValue] = React.useState<
    number | string | undefined
  >(value);

  const update = () => {
    setErrorText("");

    if (!isActionTimeoutValid(currentValue)) {
      throw new Error(HELP_TEXT);
    }

    onChange({
      timeout: currentValue ?? null,
    } as UpdateType);
  };

  return (
    <ListItem
      onMouseOver={() => {
        if (canEdit) {
          setShowEditableIcon(true);
        }
      }}
      onMouseLeave={() => {
        setShowEditableIcon(false);
      }}
      disablePadding={disablePadding}
    >
      <ListItemText
        primaryTypographyProps={{
          component: "span",
        }}
        sx={{
          mt: disablePadding ? 0 : theme.spacing(0.5),
        }}
        primary={
          <>
            <span>Timeout</span>

            {onHelperClick && (
              <IconButton
                sx={{
                  p: "5px",
                  verticalAlign: "middle",
                  ml: theme.spacing(0.25),
                }}
                onClick={() => onHelperClick()}
              >
                <InfoIcon
                  color="primary"
                  sx={{
                    fontSize: "1.1rem",
                    cursor: "pointer",
                  }}
                />
              </IconButton>
            )}

            <EditFieldButton
              editable={editable}
              showEditableIcon={showEditableIcon}
              onClick={() => {
                if (editable) {
                  update();
                }
                setEditable(!editable);
              }}
            />

            <Box>
              {editable === false ? (
                <Typography
                  component="span"
                  variant="body2"
                  style={{
                    whiteSpace: "pre-wrap",
                    color: theme.palette.text.secondary,
                    wordBreak: "break-word",
                  }}
                >
                  <>
                    {isNotNil(currentValue)
                      ? `${currentValue} ${pluralize("minute", parseTimeout(currentValue))}`
                      : "None"}
                  </>
                </Typography>
              ) : (
                <TextField
                  sx={{ mt: theme.spacing(1) }}
                  size="small"
                  variant="outlined"
                  value={currentValue ?? ""}
                  type="number"
                  helperText={
                    isActionTimeoutValid(currentValue) ? null : HELP_TEXT
                  }
                  error={!isActionTimeoutValid(currentValue)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleTimeoutOnChangeState(event, setCurrentValue);
                  }}
                />
              )}
            </Box>
          </>
        }
      />
    </ListItem>
  );
}
